import { connect, useSelector } from 'react-redux';
import { Field, getFormValues, InjectedFormProps, reduxForm } from 'redux-form';

import InputField from 'dashboard/components/FormControls/InputField';
import ReactSelect, { OptionType } from 'dashboard/components/ReactSelect';
import FormSection from 'dashboard/layouts/FormLayout/Section';
import { ExternalIntegrationDataSource } from 'dashboard/models/external-integration';
import { isTableSchemaEqual } from 'dashboard/models/importer';
import { RootState } from 'dashboard/redux/store';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { Label, Paragraph } from 'design-system/components/Text';

interface FormCloneImporterProps {
  handleClose: () => void;
  oldName: string;
  dataSourceOptions: OptionType[];
  oldDataSource: ExternalIntegrationDataSource | undefined;
  dataSources: ExternalIntegrationDataSource[];
}

const FormCloneImporter = ({
  submitting,
  handleSubmit,
  handleClose,
  dataSourceOptions,
  oldDataSource,
  dataSources,
}: FormCloneImporterProps & InjectedFormProps<FormCloneImporterData, FormCloneImporterProps>) => {
  const ReduxFormSelect = (props: any) => {
    const { input, options } = props;

    return (
      <ReactSelect
        {...input}
        onChange={(value) => input.onChange(value)}
        onBlur={() => input.onBlur(input.value)}
        options={options}
        isClearable={false}
        placeholder='Data Source'
        portal
      />
    );
  };

  const formValues = useSelector<RootState, any>(getFormValues('CloneImporter'));

  const newDataSource = dataSources.find((d) => d.id === formValues?.dataSourceId);

  const isTableSchemaSame = isTableSchemaEqual(oldDataSource, newDataSource);

  return (
    <form onSubmit={handleSubmit}>
      <FormSection>
        <Field
          label='Name'
          name='name'
          id='name'
          disabled={submitting}
          component={InputField}
          placeholder='Name'
          type='text'
          data-test='clone-importer-form__name'
          autoFocus
        />
        <Label size='md' weight='bold'>
          Data Source
        </Label>
        <Spacer height={1} />
        <Field
          label='Data Source'
          name='dataSourceId'
          id='clone_importer_form__data-source'
          disabled={submitting}
          component={ReduxFormSelect}
          options={dataSourceOptions}
        />
      </FormSection>
      {!isTableSchemaSame && (
        <>
          <Spacer height={1} />
          <Paragraph>
            Warning: This data source has a different table schema than the original data source.
            Field mappings won&apos;t be cloned.
          </Paragraph>
        </>
      )}
      <Spacer height={3} />
      <HStack equal={true}>
        <Button
          variant='basic'
          id='clone-importer-form__cancel'
          data-test='clone-importer-form__cancel'
          disabled={submitting}
          onClick={handleClose}
        >
          Cancel
        </Button>
        <Button
          type='submit'
          id='clone-importer-form__confirm'
          data-test='clone-importer-form__confirm'
          disabled={submitting}
        >
          Create
        </Button>
      </HStack>
    </form>
  );
};

export interface FormCloneImporterData {
  name: string;
  dataSourceId: string;
}

const mapStateToProps = (_state: RootState, ownProps: FormCloneImporterProps) => {
  return {
    initialValues: {
      name: `Copy of ${ownProps.oldName}`,
      dataSourceId: ownProps.oldDataSource?.id || '',
    },
  };
};

export default connect(mapStateToProps)(
  reduxForm<FormCloneImporterData, FormCloneImporterProps>({
    form: 'CloneImporter',
  })(FormCloneImporter),
);
